import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { postService, tagService, uploadService } from '../api/services';
import BlockEditor from '../components/BlockEditor';
import FileUpload from '../components/FileUpload';
import { parseContent } from '../components/BlockRenderer';
import './CreatePostPage.css';

const EditPostPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    tags: [],
    customTag: '',
    post_type: 'DISCUSSION',
    next_post_id: '',
  });
  const [blocks, setBlocks] = useState([{ type: 'text', content: '' }]);
  const [attachments, setAttachments] = useState([]);
  const [allTags, setAllTags] = useState([]);
  // 下一篇推薦選擇器用：同頻道的文章列表（排除本文自身）
  const [channelPostsList, setChannelPostsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [postRes, tagsRes, attachRes] = await Promise.all([
          postService.get(id),
          tagService.list(),
          uploadService.listByPost(id),
        ]);
        const post = postRes.data;
        setFormData({
          title: post.title,
          tags: post.tags?.map((t) => t.id) || [],
          customTag: '',
          post_type: post.post_type || 'DISCUSSION',
          next_post_id: post.next_post_id || '',
        });
        setBlocks(parseContent(post.content));
        setAttachments((attachRes.data || []).map(a => ({ ...a, url: uploadService.getFileUrl(`/api/uploads/file/${a.file_path}`) })));
        setAllTags(tagsRes.data);
        // 載入同頻道文章供「推薦接著看」選擇（排除本文自身，避免自我推薦）；查詢失敗不阻斷編輯
        try {
          const listRes = await postService.list({ channel: post.channel_id, limit: 50 });
          setChannelPostsList((listRes.data || []).filter((p) => p.id !== id));
        } catch (listErr) {
          setChannelPostsList([]);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleTagToggle = (tagId) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.includes(tagId)
        ? prev.tags.filter((id) => id !== tagId)
        : [...prev.tags, tagId],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const hasContent = blocks.some(b => (b.content && b.content.trim()) || b.type === 'image');
    if (!formData.title.trim() || !hasContent) {
      setError('標題和內容不能為空');
      return;
    }

    // 系統標籤與自訂標籤擇一即可（與發文頁一致），不強制一定要選系統標籤
    if (formData.tags.length === 0 && !formData.customTag.trim()) {
      setError('至少選擇一個標籤，或填寫一個自訂標籤');
      return;
    }

    setSubmitting(true);

    try {
      const content = JSON.stringify(blocks);
      await postService.update(id, { ...formData, content });
      // Link new attachments
      await Promise.all(attachments.filter(a => !a.post_id).map(a => uploadService.linkToPost(a.id, id)));
      navigate(`/posts/${id}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="container mt-5">載入中...</div>;

  return (
    <div className="create-post-page">
      <div className="container">
        <h1>編輯文章</h1>

        {error && <div className="alert alert-danger">{error}</div>}

        <form onSubmit={handleSubmit} className="post-form">
          <div className="form-group">
            <label htmlFor="title">標題 *</label>
            <input
              type="text"
              id="title"
              name="title"
              className="form-control"
              value={formData.title}
              onChange={handleChange}
              required
              disabled={submitting}
            />
          </div>

          <div className="form-group">
            <label htmlFor="post_type">文章類型 *</label>
            <select
              id="post_type"
              name="post_type"
              className="form-control"
              value={formData.post_type}
              onChange={handleChange}
              required
              disabled={submitting}
            >
              <option value="DISCUSSION">討論中</option>
              <option value="EXPERIENCE">經驗分享</option>
            </select>
          </div>

          <div className="form-group">
            <label>內容 *</label>
            <BlockEditor blocks={blocks} onChange={setBlocks} />
          </div>

          <div className="form-group">
            <label>附件上傳</label>
            <FileUpload attachments={attachments} onAttachmentsChange={setAttachments} />
          </div>

          <div className="form-group">
            <label>選擇標籤 (可多選，與下方自訂標籤擇一即可)</label>
            <div className="tags-selector">
              {allTags.map((tag) => (
                <label key={tag.id} className="tag-checkbox">
                  <input
                    type="checkbox"
                    checked={formData.tags.includes(tag.id)}
                    onChange={() => handleTagToggle(tag.id)}
                    disabled={submitting}
                  />
                  {tag.name}
                </label>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="customTag">自訂標籤（與系統標籤擇一即可，新增一個）</label>
            <input
              type="text"
              id="customTag"
              name="customTag"
              className="form-control"
              value={formData.customTag}
              onChange={handleChange}
              placeholder="輸入自訂標籤（只能輸入一個）"
              disabled={submitting}
            />
          </div>

          <div className="form-group">
            <label htmlFor="next_post_id">推薦接著看<span className="optional-hint">（選填，可引導讀者閱讀相關文章）</span></label>
            <select
              id="next_post_id"
              name="next_post_id"
              className="form-control"
              value={formData.next_post_id}
              onChange={handleChange}
              disabled={submitting}
            >
              <option value="">-- 無推薦 --</option>
              {channelPostsList.map((post) => (
                <option key={post.id} value={post.id}>
                  {post.title}
                </option>
              ))}
            </select>
          </div>

          <div className="form-actions">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting}
            >
              {submitting ? '更新中...' : '更新'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate(`/posts/${id}`)}
              disabled={submitting}
            >
              取消
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditPostPage;

